import React from 'react';
import { BookOpen, Flame, Trophy } from 'lucide-react';

export interface UserStats {
  totalStudyTime: number; // Not used yet - for future study time tracking
  completedModules: number;
  currentStreak: number;
  longestStreak: number;
  averageSessionTime: number; // Not used yet - for future study time tracking
  studyHistory: {
    date: string;
    minutes: number; // Used for display purposes only
  }[];
}

interface StatsSectionProps {
  stats: UserStats;
  isLoading: boolean;
  onRetry?: () => void;
}

const StatsSection: React.FC<StatsSectionProps> = ({ stats, isLoading, onRetry }) => {
  const formatNumber = (num: number): string => {
    return num.toLocaleString('cs-CZ');
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-gray-100 dark:bg-gray-700 rounded-2xl p-5 animate-pulse">
            <div className="flex flex-col items-center space-y-2">
              <div className="h-8 w-8 bg-gray-200 dark:bg-gray-600 rounded-full"></div>
              <div className="h-3 w-20 bg-gray-200 dark:bg-gray-600 rounded"></div>
              <div className="h-7 w-12 bg-gray-200 dark:bg-gray-600 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800 rounded-2xl p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
          <div className="flex flex-col items-center text-center space-y-1">
            <BookOpen className="w-7 h-7 text-green-600 dark:text-green-400" strokeWidth={2.5} />
            <p className="text-xs font-bold uppercase tracking-wider text-green-600 dark:text-green-400">
              Dokončené moduly
            </p>
            <p className="text-2xl font-bold text-green-900 dark:text-green-100">
              {formatNumber(stats.completedModules)}
            </p>
          </div>
        </div>

        <div className="bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-200 dark:border-orange-800 rounded-2xl p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
          <div className="flex flex-col items-center text-center space-y-1">
            <Flame className="w-7 h-7 text-orange-600 dark:text-orange-400" strokeWidth={2.5} />
            <p className="text-xs font-bold uppercase tracking-wider text-orange-600 dark:text-orange-400">
              Aktuální série
            </p>
            <p className="text-2xl font-bold text-orange-900 dark:text-orange-100">
              {formatNumber(stats.currentStreak)} dní
            </p>
          </div>
        </div>

        <div className="bg-purple-50 dark:bg-purple-900/20 border-2 border-purple-200 dark:border-purple-800 rounded-2xl p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
          <div className="flex flex-col items-center text-center space-y-1">
            <Trophy className="w-7 h-7 text-purple-600 dark:text-purple-400" strokeWidth={2.5} />
            <p className="text-xs font-bold uppercase tracking-wider text-purple-600 dark:text-purple-400">
              Nejdelší série
            </p>
            <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
              {formatNumber(stats.longestStreak)} dní
            </p>
          </div>
        </div>
      </div>

      {/* Retry button */}
      {onRetry && (
        <div className="text-center">
          <button
            onClick={onRetry}
            className="px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-bold rounded-2xl border-b-4 border-b-amber-600 active:border-b-0 active:translate-y-1 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-gray-800"
          >
            Zkusit znovu
          </button>
        </div>
      )}
    </div>
  );
};

export default StatsSection;